import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import {
    getKitchenOrders,
    setFoodOrderStatus,
    setFoodOrderSchedule,
    setFoodOrderUnitStatus,
} from "./../../../api/foodApi";

import type {
    FoodOrder,
    FoodOrderStatus,
} from "./foodTypes";

import {
    estimateOrderMinutes,
    formatEstimatedTime,
    getEstimatedReadyAt,
    getOrderBoxCount,
    MICROWAVE_COUNT,
    MINUTES_PER_BOX,
} from "./foodEstimate";

import "./playerLoungeFood.css";

type KitchenMode = "simple" | "advanced";

type KitchenColumnKey =
    | "PreOrder"
    | "Received"
    | "InProgress"
    | "Microwave"
    | "Ready";

type MicrowaveAssignment = {
    oven: string;
    startedAt: string;
};

const MOBILE_VISIBLE_COUNT = 3;

function normalizeStatus(
    status: FoodOrder["status"]
): FoodOrderStatus {
    if (
        status === "Received" ||
        status === "InProgress" ||
        status === "Ready" ||
        status === "Delivered" ||
        status === "Cancelled"
    ) {
        return status;
    }

    switch (Number(status)) {
        case 0:
            return "Received";
        case 1:
            return "InProgress";
        case 2:
            return "Ready";
        case 3:
            return "Delivered";
        case 4:
            return "Cancelled";
        default:
            return "Received";
    }
}

function nextStatus(
    status: FoodOrderStatus
): FoodOrderStatus | null {
    if (status === "Received")
        return "InProgress";

    if (status === "InProgress")
        return "Ready";

    if (status === "Ready")
        return "Delivered";

    return null;
}

function previousStatus(
    status: FoodOrderStatus
): FoodOrderStatus | null {
    if (status === "Ready")
        return "InProgress";

    if (status === "InProgress")
        return "Received";

    return null;
}

function formatTime(value: string | Date) {
    return new Date(value).toLocaleTimeString(
        "da-DK",
        {
            hour: "2-digit",
            minute: "2-digit",
        }
    );
}

function isPreOrder(order: FoodOrder) {
    if (!order.scheduledFor)
        return false;

    const scheduledTime =
        new Date(order.scheduledFor).getTime();

    if (!Number.isFinite(scheduledTime))
        return false;

    return scheduledTime > Date.now();
}

function formatScheduledDateTime(value: string | Date) {
    return new Date(value).toLocaleString(
        "da-DK",
        {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        }
    );
}

export default function KitchenScreen() {
    const { eventId } = useParams();

    const parsedEventId = Number(eventId);

    const [orders, setOrders] =
        useState<FoodOrder[]>([]);

    const [error, setError] =
        useState("");

    const [updatingOrderId, setUpdatingOrderId] =
        useState<number | null>(null);

    const [updatingUnitId, setUpdatingUnitId] =
        useState<number | null>(null);

    const [mode, setMode] =
        useState<KitchenMode>(() => {
            const saved =
                window.localStorage.getItem(
                    "playerLoungeKitchenMode"
                );

            return saved === "advanced"
                ? "advanced"
                : "simple";
        });

    const [microwaveAssignments, setMicrowaveAssignments] =
        useState<Record<number, MicrowaveAssignment>>(
            () => {
                try {
                    const saved =
                        window.localStorage.getItem(
                            `playerLoungeMicrowave-${eventId ?? "unknown"}`
                        );

                    return saved
                        ? JSON.parse(saved)
                        : {};
                } catch {
                    return {};
                }
            }
        );

    const [expandedColumns, setExpandedColumns] =
        useState<Record<string, boolean>>({});

    const [isCompact, setIsCompact] =
        useState(
            () => window.innerWidth <= 1100
        );

    const [clockTick, setClockTick] =
        useState(0);

    useEffect(() => {
        const onResize = () => {
            setIsCompact(
                window.innerWidth <= 1100
            );
        };

        window.addEventListener(
            "resize",
            onResize
        );

        return () =>
            window.removeEventListener(
                "resize",
                onResize
            );
    }, []);

    useEffect(() => {
        const timer =
            window.setInterval(
                () => {
                    setClockTick(
                        value =>
                            value + 1
                    );
                },
                1000
            );

        return () =>
            window.clearInterval(
                timer
            );
    }, []);

    useEffect(() => {
        window.localStorage.setItem(
            "playerLoungeKitchenMode",
            mode
        );
    }, [mode]);

    useEffect(() => {
        window.localStorage.setItem(
            `playerLoungeMicrowave-${eventId ?? "unknown"}`,
            JSON.stringify(
                microwaveAssignments
            )
        );
    }, [
        microwaveAssignments,
        eventId,
    ]);

    const load = useCallback(async () => {
        if (
            !Number.isFinite(parsedEventId) ||
            parsedEventId <= 0
        ) {
            setError(
                "Event ID mangler eller er ugyldigt."
            );

            return;
        }

        try {
            const data =
                await getKitchenOrders(
                    parsedEventId
                );

            const normalized =
                data.map(order => ({
                    ...order,
                    status:
                        normalizeStatus(
                            order.status
                        ),
                    units:
                        (order.units ?? []).map(
                            unit => ({
                                ...unit,
                                status:
                                    normalizeStatus(
                                        unit.status
                                    ),
                            })
                        ),
                }));

            setOrders(normalized);

            // Ryd mikroovn-markering hvis ordren
            // ikke længere er i gang.
            setMicrowaveAssignments(
                previous => {
                    const activeIds =
                        new Set(
                            normalized
                                .filter(
                                    order =>
                                        normalizeStatus(
                                            order.status
                                        ) ===
                                        "InProgress"
                                )
                                .map(
                                    order =>
                                        order.id
                                )
                        );

                    const next = {
                        ...previous,
                    };

                    let changed = false;

                    Object.keys(next).forEach(
                        key => {
                            const id =
                                Number(key);

                            if (
                                !activeIds.has(
                                    id
                                )
                            ) {
                                delete next[id];
                                changed = true;
                            }
                        }
                    );

                    return changed
                        ? next
                        : previous;
                }
            );

            setError("");
        } catch (err) {
            console.error(
                "Kunne ikke hente køkkenordrer:",
                err
            );

            setError(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke hente ordrer."
            );
        }
    }, [parsedEventId]);

    useEffect(() => {
        load();

        const timer =
            window.setInterval(
                load,
                5000
            );

        return () =>
            window.clearInterval(
                timer
            );
    }, [load]);

    async function changeStatus(
        order: FoodOrder,
        status: FoodOrderStatus
    ) {
        setUpdatingOrderId(
            order.id
        );

        try {
            const updated =
                await setFoodOrderStatus(
                    parsedEventId,
                    order.id,
                    status
                );

            setOrders(prev =>
                prev.map(x =>
                    x.id === updated.id
                        ? {
                            ...updated,
                            status:
                                normalizeStatus(
                                    updated.status
                                ),
                        }
                        : x
                )
            );

            if (
                status !==
                "InProgress"
            ) {
                setMicrowaveAssignments(
                    prev => {
                        const next = {
                            ...prev,
                        };

                        delete next[
                            order.id
                        ];

                        return next;
                    }
                );
            }
        } catch (err) {
            console.error(
                "Kunne ikke ændre ordrestatus:",
                err
            );

            setError(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke ændre ordrestatus."
            );
        } finally {
            setUpdatingOrderId(
                null
            );
        }
    }

    async function changeUnitStatus(
        order: FoodOrder,
        unitId: number,
        status: FoodOrderStatus
    ) {
        setUpdatingUnitId(unitId);
        setError("");

        try {
            const updated =
                await setFoodOrderUnitStatus(
                    parsedEventId,
                    order.id,
                    unitId,
                    status
                );

            const normalizedUpdated = {
                ...updated,
                status:
                    normalizeStatus(
                        updated.status
                    ),
                units:
                    (updated.units ?? []).map(
                        unit => ({
                            ...unit,
                            status:
                                normalizeStatus(
                                    unit.status
                                ),
                        })
                    ),
            };

            setOrders(prev =>
                prev.map(x =>
                    x.id === updated.id
                        ? normalizedUpdated
                        : x
                )
            );
        } catch (err) {
            console.error(
                "Kunne ikke ændre boksstatus:",
                err
            );

            setError(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke ændre status på boksen."
            );
        } finally {
            setUpdatingUnitId(null);
        }
    }

    function getUnitStatusText(
        status: FoodOrderStatus
    ) {
        if (status === "Received")
            return "Ny";

        if (status === "InProgress")
            return "I gang";

        if (status === "Ready")
            return "Klar";

        if (status === "Delivered")
            return "Udleveret";

        if (status === "Cancelled")
            return "Annulleret";

        return status;
    }

    async function advance(
        order: FoodOrder
    ) {
        const status =
            normalizeStatus(
                order.status
            );

        const next =
            nextStatus(status);

        if (!next)
            return;

        await changeStatus(
            order,
            next
        );
    }

    async function goBack(
        order: FoodOrder
    ) {
        const status =
            normalizeStatus(
                order.status
            );

        const previous =
            previousStatus(
                status
            );

        if (!previous)
            return;

        // Hvis ordren står i mikroovn,
        // fjernes mikroovn-markeringen også.
        if (
            microwaveAssignments[
            order.id
            ]
        ) {
            setMicrowaveAssignments(
                prev => {
                    const next = {
                        ...prev,
                    };

                    delete next[
                        order.id
                    ];

                    return next;
                }
            );
        }

        await changeStatus(
            order,
            previous
        );
    }

    async function releasePreOrder(
        order: FoodOrder
    ) {
        setUpdatingOrderId(
            order.id
        );

        try {
            const updated =
                await setFoodOrderSchedule(
                    parsedEventId,
                    order.id,
                    null
                );

            setOrders(prev =>
                prev.map(x =>
                    x.id === updated.id
                        ? {
                            ...updated,
                            status:
                                normalizeStatus(
                                    updated.status
                                ),
                        }
                        : x
                )
            );
        } catch (err) {
            console.error(
                "Kunne ikke frigive forudbestilling:",
                err
            );

            setError(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke frigive forudbestillingen."
            );
        } finally {
            setUpdatingOrderId(
                null
            );
        }
    }

    function putInMicrowave(
        order: FoodOrder
    ) {
        const oven =
            window.prompt(
                `Hvilken mikroovn er ordre #${order.id} sat i?\n\nFx: 1, 2, 3 eller "Venstre"`
            );

        if (
            oven === null ||
            oven.trim() === ""
        ) {
            return;
        }

        setMicrowaveAssignments(
            prev => ({
                ...prev,
                [order.id]: {
                    oven:
                        oven.trim(),
                    startedAt:
                        new Date().toISOString(),
                },
            })
        );
    }

    function removeFromMicrowave(
        order: FoodOrder
    ) {
        setMicrowaveAssignments(
            prev => {
                const next = {
                    ...prev,
                };

                delete next[
                    order.id
                ];

                return next;
            }
        );
    }

    async function cancelOrder(
        order: FoodOrder
    ) {
        if (
            !window.confirm(
                `Vil du annullere ordre #${order.id}?`
            )
        ) {
            return;
        }

        await changeStatus(
            order,
            "Cancelled"
        );
    }

    const activeOrders =
        useMemo(
            () =>
                orders.filter(order => {
                    const status =
                        normalizeStatus(
                            order.status
                        );

                    return (
                        status !==
                        "Delivered" &&
                        status !==
                        "Cancelled"
                    );
                }),
            [orders]
        );

    const grouped =
        useMemo(() => {
            const result: Record<
                KitchenColumnKey,
                FoodOrder[]
            > = {
                PreOrder: [],
                Received: [],
                InProgress: [],
                Microwave: [],
                Ready: [],
            };

            activeOrders.forEach(
                order => {
                    const status =
                        normalizeStatus(
                            order.status
                        );

                    if (
                        status ===
                        "Received"
                    ) {
                        if (
                            isPreOrder(
                                order
                            )
                        ) {
                            result.PreOrder.push(
                                order
                            );
                        } else {
                            result.Received.push(
                                order
                            );
                        }
                    }

                    if (
                        status ===
                        "InProgress"
                    ) {
                        if (
                            mode ===
                            "advanced" &&
                            microwaveAssignments[
                            order.id
                            ]
                        ) {
                            result.Microwave.push(
                                order
                            );
                        } else {
                            result.InProgress.push(
                                order
                            );
                        }
                    }

                    if (
                        status ===
                        "Ready"
                    ) {
                        result.Ready.push(
                            order
                        );
                    }
                }
            );

            (
                Object.keys(
                    result
                ) as KitchenColumnKey[]
            ).forEach(key => {
                result[key].sort(
                    (a, b) => {
                        if (
                            key ===
                            "PreOrder"
                        ) {
                            return (
                                new Date(
                                    a.scheduledFor ??
                                    a.createdAt
                                ).getTime() -
                                new Date(
                                    b.scheduledFor ??
                                    b.createdAt
                                ).getTime()
                            );
                        }

                        return (
                            new Date(
                                a.createdAt
                            ).getTime() -
                            new Date(
                                b.createdAt
                            ).getTime()
                        );
                    }
                );
            });

            return result;
        }, [
            activeOrders,
            mode,
            microwaveAssignments,
            clockTick,
        ]);

    const columns: {
        key: KitchenColumnKey;
        title: string;
    }[] =
        mode === "advanced"
            ? [
                {
                    key: "PreOrder",
                    title: "Forudbestilling",
                },
                {
                    key: "Received",
                    title: "Nye ordrer",
                },
                {
                    key: "InProgress",
                    title: "I gang",
                },
                {
                    key: "Microwave",
                    title: "Mikroovn",
                },
                {
                    key: "Ready",
                    title: "Klar",
                },
            ]
            : [
                {
                    key: "PreOrder",
                    title: "Forudbestilling",
                },
                {
                    key: "Received",
                    title: "Nye ordrer",
                },
                {
                    key: "InProgress",
                    title: "I gang",
                },
                {
                    key: "Ready",
                    title: "Klar",
                },
            ];

    function visibleOrders(
        key: KitchenColumnKey,
        items: FoodOrder[]
    ) {
        if (
            !isCompact ||
            expandedColumns[key]
        ) {
            return items;
        }

        return items.slice(
            0,
            MOBILE_VISIBLE_COUNT
        );
    }

    function toggleColumn(
        key: KitchenColumnKey
    ) {
        setExpandedColumns(
            prev => ({
                ...prev,
                [key]:
                    !prev[key],
            })
        );
    }

    const boardStyle:
        React.CSSProperties = {
        display: "grid",
        gridTemplateColumns:
            isCompact
                ? "1fr"
                : `repeat(${columns.length}, minmax(260px, 1fr))`,
        gap: "16px",
        alignItems: "start",
    };

    const orderActionButtonStyle: React.CSSProperties = {
        flex: "1 1 140px",
        minWidth: "120px",
    };

    return (
        <div className="pl-kitchen-page">
            <header className="pl-kitchen-header">
                <div>
                    <h1>
                        Køkken skærm
                    </h1>

                    <span>
                        Player Lounge · Event{" "}
                        {parsedEventId}
                        {" · "}
                        {MICROWAVE_COUNT} mikroovne
                        {" · "}
                        {MINUTES_PER_BOX} min./boks
                        {" · "}
                        Prioritet: Mikroovn → I gang → Nye
                    </span>
                </div>

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                        justifyContent:
                            "flex-end",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            gap: "4px",
                            padding: "4px",
                            borderRadius: "10px",
                            background:
                                "rgba(255,255,255,0.08)",
                        }}
                    >
                        <button
                            type="button"
                            onClick={() =>
                                setMode(
                                    "simple"
                                )
                            }
                            style={{
                                border: 0,
                                borderRadius:
                                    "8px",
                                padding:
                                    "8px 12px",
                                cursor:
                                    "pointer",
                                fontWeight:
                                    700,
                                opacity:
                                    mode ===
                                        "simple"
                                        ? 1
                                        : 0.55,
                            }}
                        >
                            Simple
                        </button>

                        <button
                            type="button"
                            onClick={() =>
                                setMode(
                                    "advanced"
                                )
                            }
                            style={{
                                border: 0,
                                borderRadius:
                                    "8px",
                                padding:
                                    "8px 12px",
                                cursor:
                                    "pointer",
                                fontWeight:
                                    700,
                                opacity:
                                    mode ===
                                        "advanced"
                                        ? 1
                                        : 0.55,
                            }}
                        >
                            Advanced
                        </button>
                    </div>

                    <div className="pl-online">
                        ● Live
                    </div>
                </div>
            </header>

            {error && (
                <div className="pl-message">
                    {error}
                </div>
            )}

            <div
                className="pl-board"
                style={boardStyle}
            >
                {columns.map(
                    column => {
                        const columnOrders =
                            grouped[
                            column.key
                            ];

                        const shownOrders =
                            visibleOrders(
                                column.key,
                                columnOrders
                            );

                        const hasMore =
                            isCompact &&
                            columnOrders.length >
                            MOBILE_VISIBLE_COUNT;

                        const isExpanded =
                            !!expandedColumns[
                            column.key
                            ];

                        return (
                            <section
                                className={`pl-board-column status-${column.key}`}
                                key={
                                    column.key
                                }
                                style={{
                                    minWidth: 0,
                                    width: "100%",
                                }}
                            >
                                <h2>
                                    <div>
                                        {
                                            column.title
                                        }

                                        <span>
                                            {
                                                columnOrders.length
                                            }
                                        </span>
                                    </div>
                                </h2>

                                <div className="pl-board-list">
                                    {shownOrders.map(
                                        order => {
                                            const status =
                                                normalizeStatus(
                                                    order.status
                                                );

                                            const microwave =
                                                microwaveAssignments[
                                                order.id
                                                ];

                                            const units =
                                                order.units ?? [];

                                            const hasUnitWorkflow =
                                                order.splitOrder &&
                                                units.length > 0;

                                            const readyUnitCount =
                                                units.filter(
                                                    unit =>
                                                        normalizeStatus(
                                                            unit.status
                                                        ) ===
                                                        "Ready"
                                                ).length;

                                            const inProgressUnitCount =
                                                units.filter(
                                                    unit =>
                                                        normalizeStatus(
                                                            unit.status
                                                        ) ===
                                                        "InProgress"
                                                ).length;

                                            return (
                                                <article
                                                    className="pl-order-card"
                                                    key={
                                                        order.id
                                                    }
                                                >
                                                    <div className="pl-order-card-top">
                                                        <strong>
                                                            #
                                                            {
                                                                order.id
                                                            }
                                                        </strong>

                                                        <span>
                                                            {formatTime(
                                                                order.createdAt
                                                            )}
                                                        </span>
                                                    </div>

                                                    {order.personName && (
                                                        <div className="pl-person">
                                                            {
                                                                order.personName
                                                            }
                                                        </div>
                                                    )}
                                                    <div
                                                        style={{
                                                            marginTop: "6px",
                                                            marginBottom: "8px",
                                                            fontSize: "13px",
                                                            fontWeight: 700,
                                                            opacity: 0.8,
                                                        }}
                                                    >
                                                        {order.splitOrder
                                                            ? `🏷️ ${getOrderBoxCount(order)} separate labels`
                                                            : "🏷️ 1 samlet label"}
                                                    </div>


                                                    {(() => {
                                                        const minutes =
                                                            estimateOrderMinutes(
                                                                order,
                                                                activeOrders,
                                                                new Date(),
                                                                {
                                                                    microwaveOrderIds:
                                                                        Object.keys(
                                                                            microwaveAssignments
                                                                        ).map(Number),
                                                                }
                                                            );

                                                        if (
                                                            minutes <= 0 ||
                                                            column.key === "PreOrder"
                                                        ) {
                                                            return null;
                                                        }

                                                        const readyAt =
                                                            getEstimatedReadyAt(
                                                                minutes
                                                            );

                                                        return (
                                                            <div
                                                                style={{
                                                                    margin:
                                                                        "8px 0 10px",
                                                                    padding:
                                                                        "9px 11px",
                                                                    borderRadius:
                                                                        "8px",
                                                                    background:
                                                                        "rgba(65, 105, 225, 0.10)",
                                                                }}
                                                            >
                                                                <strong>
                                                                    ⏱ Ca. klar kl.{" "}
                                                                    {formatEstimatedTime(
                                                                        readyAt
                                                                    )}
                                                                </strong>

                                                                <span
                                                                    style={{
                                                                        display:
                                                                            "block",
                                                                        marginTop:
                                                                            "3px",
                                                                        fontSize:
                                                                            "12px",
                                                                        opacity:
                                                                            0.75,
                                                                    }}
                                                                >
                                                                    Ca.{" "}
                                                                    {minutes} min.
                                                                    {" · "}
                                                                    {getOrderBoxCount(
                                                                        order
                                                                    )}{" "}
                                                                    boks
                                                                    {getOrderBoxCount(
                                                                        order
                                                                    ) === 1
                                                                        ? ""
                                                                        : "e"}
                                                                </span>
                                                            </div>
                                                        );
                                                    })()}

                                                    {column.key ===
                                                        "PreOrder" &&
                                                        order.scheduledFor && (
                                                            <div
                                                                style={{
                                                                    margin:
                                                                        "8px 0 10px",
                                                                    padding:
                                                                        "10px 12px",
                                                                    borderRadius:
                                                                        "8px",
                                                                    background:
                                                                        "rgba(65, 105, 225, 0.15)",
                                                                    fontWeight:
                                                                        800,
                                                                }}
                                                            >
                                                                🕒 Klar til{" "}
                                                                {formatScheduledDateTime(
                                                                    order.scheduledFor
                                                                )}
                                                            </div>
                                                        )}

                                                    {column.key ===
                                                        "Microwave" &&
                                                        microwave && (
                                                            <div
                                                                style={{
                                                                    margin:
                                                                        "8px 0 10px",
                                                                    padding:
                                                                        "10px 12px",
                                                                    borderRadius:
                                                                        "8px",
                                                                    background:
                                                                        "rgba(255, 170, 0, 0.15)",
                                                                    fontWeight:
                                                                        800,
                                                                }}
                                                            >
                                                                🔥 Mikroovn{" "}
                                                                {
                                                                    microwave.oven
                                                                }
                                                                <span
                                                                    style={{
                                                                        display:
                                                                            "block",
                                                                        marginTop:
                                                                            "3px",
                                                                        fontSize:
                                                                            "12px",
                                                                        fontWeight:
                                                                            500,
                                                                        opacity:
                                                                            0.75,
                                                                    }}
                                                                >
                                                                    Sat ind kl.{" "}
                                                                    {formatTime(
                                                                        microwave.startedAt
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}

                                                    <ul>
                                                        {order.lines.map(
                                                            (
                                                                line,
                                                                index
                                                            ) => (
                                                                <li
                                                                    key={`${line.menuItemId}-${index}`}
                                                                >
                                                                    {line.quantity >
                                                                        1
                                                                        ? `${line.quantity} × `
                                                                        : ""}
                                                                    {
                                                                        line.name
                                                                    }
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>

                                                    {hasUnitWorkflow && (
                                                        <div
                                                            style={{
                                                                margin:
                                                                    "10px 0",
                                                                padding:
                                                                    "10px",
                                                                borderRadius:
                                                                    "10px",
                                                                border:
                                                                    "1px solid rgba(0,0,0,0.12)",
                                                                display:
                                                                    "grid",
                                                                gap: "7px",
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display:
                                                                        "flex",
                                                                    justifyContent:
                                                                        "space-between",
                                                                    gap: "8px",
                                                                    alignItems:
                                                                        "center",
                                                                    flexWrap:
                                                                        "wrap",
                                                                }}
                                                            >
                                                                <strong>
                                                                    🏷️ {readyUnitCount}/{units.length} klar
                                                                </strong>

                                                                <small>
                                                                    {inProgressUnitCount >
                                                                        0
                                                                        ? `${inProgressUnitCount} i gang`
                                                                        : "Ingen i gang"}
                                                                </small>
                                                            </div>

                                                            {units
                                                                .slice()
                                                                .sort(
                                                                    (
                                                                        a,
                                                                        b
                                                                    ) =>
                                                                        a.unitNumber -
                                                                        b.unitNumber
                                                                )
                                                                .map(
                                                                    unit => {
                                                                        const unitStatus =
                                                                            normalizeStatus(
                                                                                unit.status
                                                                            );

                                                                        return (
                                                                            <div
                                                                                key={
                                                                                    unit.id
                                                                                }
                                                                                style={{
                                                                                    display:
                                                                                        "grid",
                                                                                    gridTemplateColumns:
                                                                                        "minmax(0, 1fr) auto",
                                                                                    gap: "8px",
                                                                                    alignItems:
                                                                                        "center",
                                                                                    padding:
                                                                                        "8px",
                                                                                    borderRadius:
                                                                                        "8px",
                                                                                    background:
                                                                                        "rgba(65,105,225,0.06)",
                                                                                }}
                                                                            >
                                                                                <div>
                                                                                    <strong>
                                                                                        {unit.unitNumber}/{unit.unitCount}
                                                                                        {" · "}
                                                                                        {unit.name}
                                                                                    </strong>

                                                                                    <small
                                                                                        style={{
                                                                                            display:
                                                                                                "block",
                                                                                            marginTop:
                                                                                                "2px",
                                                                                            opacity:
                                                                                                0.75,
                                                                                        }}
                                                                                    >
                                                                                        {getUnitStatusText(
                                                                                            unitStatus
                                                                                        )}
                                                                                    </small>
                                                                                </div>

                                                                                <div
                                                                                    style={{
                                                                                        display:
                                                                                            "flex",
                                                                                        gap: "5px",
                                                                                        flexWrap:
                                                                                            "wrap",
                                                                                        justifyContent:
                                                                                            "flex-end",
                                                                                    }}
                                                                                >
                                                                                    {unitStatus ===
                                                                                        "Received" && (
                                                                                            <button
                                                                                                type="button"
                                                                                                disabled={
                                                                                                    updatingUnitId ===
                                                                                                    unit.id
                                                                                                }
                                                                                                onClick={() =>
                                                                                                    changeUnitStatus(
                                                                                                        order,
                                                                                                        unit.id,
                                                                                                        "InProgress"
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                {updatingUnitId ===
                                                                                                    unit.id
                                                                                                    ? "..."
                                                                                                    : "I gang"}
                                                                                            </button>
                                                                                        )}

                                                                                    {unitStatus ===
                                                                                        "InProgress" && (
                                                                                            <button
                                                                                                type="button"
                                                                                                disabled={
                                                                                                    updatingUnitId ===
                                                                                                    unit.id
                                                                                                }
                                                                                                onClick={() =>
                                                                                                    changeUnitStatus(
                                                                                                        order,
                                                                                                        unit.id,
                                                                                                        "Ready"
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                {updatingUnitId ===
                                                                                                    unit.id
                                                                                                    ? "..."
                                                                                                    : "Klar"}
                                                                                            </button>
                                                                                        )}

                                                                                    {unitStatus ===
                                                                                        "Ready" && (
                                                                                            <button
                                                                                                type="button"
                                                                                                disabled={
                                                                                                    updatingUnitId ===
                                                                                                    unit.id
                                                                                                }
                                                                                                onClick={() =>
                                                                                                    changeUnitStatus(
                                                                                                        order,
                                                                                                        unit.id,
                                                                                                        "InProgress"
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                Tilbage
                                                                                            </button>
                                                                                        )}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                )}
                                                        </div>
                                                    )}

                                                    {order.note && (
                                                        <div className="pl-note">
                                                            Bemærkning:{" "}
                                                            {
                                                                order.note
                                                            }
                                                        </div>
                                                    )}

                                                    {hasUnitWorkflow &&
                                                        status !== "Ready" &&
                                                        column.key !== "PreOrder" && (
                                                            <div
                                                                style={{
                                                                    marginTop: "10px",
                                                                    marginBottom: "6px",
                                                                    fontSize: "12px",
                                                                    fontWeight: 700,
                                                                    opacity: 0.75,
                                                                }}
                                                            >
                                                                Status styres pr. boks
                                                            </div>
                                                        )}

                                                    <div
                                                        className="pl-kitchen-order-actions"
                                                        style={{
                                                            display: "flex",
                                                            gap: "8px",
                                                            flexWrap: "wrap",
                                                            alignItems: "stretch",
                                                        }}
                                                    >
                                                        {column.key ===
                                                            "PreOrder" && (
                                                                <button
                                                                    type="button"
                                                                    disabled={
                                                                        updatingOrderId ===
                                                                        order.id
                                                                    }
                                                                    onClick={() =>
                                                                        releasePreOrder(
                                                                            order
                                                                        )
                                                                    }
                                                                >
                                                                    {updatingOrderId ===
                                                                        order.id
                                                                        ? "Flytter..."
                                                                        : "Lav nu"}
                                                                </button>
                                                            )}

                                                        {status ===
                                                            "Received" &&
                                                            column.key !==
                                                            "PreOrder" &&
                                                            !hasUnitWorkflow && (
                                                                <button
                                                                    disabled={
                                                                        updatingOrderId ===
                                                                        order.id
                                                                    }
                                                                    onClick={() =>
                                                                        advance(
                                                                            order
                                                                        )
                                                                    }
                                                                >
                                                                    {updatingOrderId ===
                                                                        order.id
                                                                        ? "Gemmer..."
                                                                        : "Sæt i gang"}
                                                                </button>
                                                            )}

                                                        {status ===
                                                            "InProgress" &&
                                                            column.key ===
                                                            "InProgress" && (
                                                                <button
                                                                    type="button"
                                                                    style={orderActionButtonStyle}
                                                                    disabled={
                                                                        updatingOrderId ===
                                                                        order.id
                                                                    }
                                                                    onClick={() =>
                                                                        goBack(
                                                                            order
                                                                        )
                                                                    }
                                                                >
                                                                    ← Tilbage til nye ordrer
                                                                </button>
                                                            )}

                                                        {status ===
                                                            "InProgress" &&
                                                            column.key ===
                                                            "InProgress" &&
                                                            !hasUnitWorkflow && (
                                                                <>
                                                                    {mode ===
                                                                        "advanced" && (
                                                                            <button
                                                                                type="button"
                                                                                disabled={
                                                                                    updatingOrderId ===
                                                                                    order.id
                                                                                }
                                                                                onClick={() =>
                                                                                    putInMicrowave(
                                                                                        order
                                                                                    )
                                                                                }
                                                                            >
                                                                                Sæt i mikroovn
                                                                            </button>
                                                                        )}

                                                                    <button
                                                                        disabled={
                                                                            updatingOrderId ===
                                                                            order.id
                                                                        }
                                                                        onClick={() =>
                                                                            changeStatus(
                                                                                order,
                                                                                "Ready"
                                                                            )
                                                                        }
                                                                    >
                                                                        {updatingOrderId ===
                                                                            order.id
                                                                            ? "Gemmer..."
                                                                            : "Marker som klar"}
                                                                    </button>
                                                                </>
                                                            )}

                                                        {column.key ===
                                                            "Microwave" &&
                                                            !hasUnitWorkflow && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        disabled={
                                                                            updatingOrderId ===
                                                                            order.id
                                                                        }
                                                                        onClick={() =>
                                                                            changeStatus(
                                                                                order,
                                                                                "Ready"
                                                                            )
                                                                        }
                                                                    >
                                                                        {updatingOrderId ===
                                                                            order.id
                                                                            ? "Gemmer..."
                                                                            : "Tag ud + marker klar"}
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        disabled={
                                                                            updatingOrderId ===
                                                                            order.id
                                                                        }
                                                                        onClick={() =>
                                                                            removeFromMicrowave(
                                                                                order
                                                                            )
                                                                        }
                                                                    >
                                                                        Tilbage til i gang
                                                                    </button>
                                                                </>
                                                            )}


                                                        {status ===
                                                            "Ready" && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        style={orderActionButtonStyle}
                                                                        disabled={
                                                                            updatingOrderId ===
                                                                            order.id
                                                                        }
                                                                        onClick={() =>
                                                                            goBack(
                                                                                order
                                                                            )
                                                                        }
                                                                    >
                                                                        ← Tilbage til i gang
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        style={orderActionButtonStyle}
                                                                        disabled={
                                                                            updatingOrderId ===
                                                                            order.id
                                                                        }
                                                                        onClick={() =>
                                                                            changeStatus(
                                                                                order,
                                                                                "Delivered"
                                                                            )
                                                                        }
                                                                    >
                                                                        {updatingOrderId ===
                                                                            order.id
                                                                            ? "Gemmer..."
                                                                            : "Udleveret"}
                                                                    </button>
                                                                </>
                                                            )}

                                                        {status !==
                                                            "Delivered" &&
                                                            status !==
                                                            "Cancelled" &&
                                                            status !==
                                                            "Ready" && (
                                                                <button
                                                                    type="button"
                                                                    className="pl-cancel-order-button"
                                                                    style={orderActionButtonStyle}
                                                                    disabled={
                                                                        updatingOrderId ===
                                                                        order.id
                                                                    }
                                                                    onClick={() =>
                                                                        cancelOrder(
                                                                            order
                                                                        )
                                                                    }
                                                                >
                                                                    Annuller
                                                                </button>
                                                            )}
                                                    </div>
                                                </article>
                                            );
                                        }
                                    )}

                                    {columnOrders.length ===
                                        0 && (
                                            <div className="pl-empty">
                                                Ingen ordrer
                                            </div>
                                        )}

                                    {hasMore && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                toggleColumn(
                                                    column.key
                                                )
                                            }
                                            style={{
                                                width: "100%",
                                                marginTop:
                                                    "8px",
                                                padding:
                                                    "10px 12px",
                                                borderRadius:
                                                    "8px",
                                                cursor:
                                                    "pointer",
                                                fontWeight:
                                                    700,
                                            }}
                                        >
                                            {isExpanded
                                                ? "Vis færre"
                                                : `Vis flere (${columnOrders.length - MOBILE_VISIBLE_COUNT})`}
                                        </button>
                                    )}
                                </div>
                            </section>
                        );
                    }
                )}
            </div>
        </div>
    );
}